import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { of, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { DialogService } from '../../dialog/dialog.service';
import { statusAsText } from '../../inspection/inspection.status';
import { RoleService } from '../../role/role.service';
import { AuthService } from '../../shared/auth.service';
import { Inspection, Inspector, Role } from '../../shared/interfaces';
import { permissions } from '../../shared/permissions';
import { UserService } from '../user.service';
import { CustomValidators, validEmailValidator } from '../../shared/validation.patterns';

@Component({
  selector: 'app-admin-form',
  templateUrl: './admin-form.component.html',
  styleUrls: ['./admin-form.component.scss'],
})
export class AdminFormComponent implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private roleService: RoleService,
    private authService: AuthService,
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private dialog: DialogService,
  ) {}

  statusAsText = statusAsText;

  user = {} as any;
  form: UntypedFormGroup;
  inspectors: Inspector[] = [];
  inspections: Inspection[] = [];

  roles: Role[];

  objectKeys = Object.keys;
  objectValues = Object.values;

  canEdit = false;

  async ngOnInit() {
    this.form = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, validEmailValidator()]],
      title: [''],
      recLetterEmail: ['', [validEmailValidator()]],
      phone: ['', CustomValidators.phone],
      active: [true],
    });

    this.form.get('email').setAsyncValidators(control => {
      return timer(500).pipe(
        switchMap(() => {
          // Ignore current saved email
          if (this.user.email && control.value === this.user.email) {
            return of(null);
          }

          return this.userService.emailAvailable(control.value).pipe(
            map(isAvailable => (!isAvailable ? { uniqueEmail: true } : null)),
            catchError(() => null),
          );
        }),
      );
    });

    this.canEdit = await this.userService.hasPermissions([permissions.USER.WRITE]);

    if (!this.canEdit) {
      this.form.disable();
    }

    this.roles = await this.roleService.list();

    this.route.data.subscribe(async (data: { user: any }) => {
      if (data.user) {
        this.user = data.user;
        this.setData(data.user);
      }
    });
  }

  // tslint:disable:forin
  setData(data: any) {
    this.form.patchValue(data);
    this.form.markAsUntouched();
    this.form.markAsPristine();

    for (const role of this.roles) {
      role.checked = !!data.roles.find(r => r.id === role.id);
    }
  }

  async submit() {
    const newUser = {
      ...this.form.value,
      userRoles: this.roles.filter(r => r.checked),
    };

    if (this.user.id) {
      if (this.user.active && !newUser.active) {
        // archiving
        this.dialog.confirm().show({
          message: `You are archiving this user. Login will no longer be allowed. Are you sure?`,
          confirmed: async () => {
            this.user = await this.userService.edit(this.user.id, newUser);
            this.setData(this.user);
            this.toastr.success('User saved!');
          },
        });
      } else if (!this.user.active && newUser.active) {
        // restoring
        this.dialog.confirm().show({
          message: `You are activating this user. Login will be allowed. Are you sure?`,
          confirmed: async () => {
            // this.user
            this.user = await this.userService.edit(this.user.id, newUser);
            this.setData(this.user);
            this.toastr.success('User saved!');
          },
        });
      } else {
        try {
          this.user = await this.userService.edit(this.user.id, newUser);

          this.setData(this.user);
          this.toastr.success('User saved!');
        } catch (e) {
          console.log('error edit', e);
        }
      }
    } else {
      try {
        const user = await this.userService.create(newUser);
        await this.authService.forgot(user.email);
        this.router.navigate(['/user/admins/form', user.id]);
        this.toastr.success('User saved!');
      } catch (e) {
        console.log('error create', e);
      }
    }
  }

  async sendResetLink(ev: Event) {
    ev.preventDefault();

    this.dialog.confirm().show({
      message: `Send password reset link to '${this.user.email}'?`,
      confirmed: async () => {
        await this.authService.forgot(this.user.email);
        this.toastr.success('E-mail sent!');
      },
    });
  }

  getValidationClass(field: string) {
    const control = this.form.controls[field];
    return {
      'is-valid': control.valid && (control.dirty || control.touched),
      'is-invalid': control.invalid && (control.dirty || control.touched),
    };
  }
}
